import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { Organization, OrganizationQuery } from '@b3networks/api/auth';
import { SellerWallet, SellerWalletService } from '@b3networks/api/billing';
import { CustomerService, PurchaseAddonModel } from '@b3networks/api/customer';
import { BundleItem, SubscriptionLicense } from '@b3networks/api/license';
import { NumberService } from '@b3networks/api/number';
import { OrderItem, OrderRequest, OrderService } from '@b3networks/api/order';
import { SalesModel } from '@b3networks/api/salemodel';
import { CPAAS_PRODUCT_ID, LinkedSellerService, Product, ProductService, Sku } from '@b3networks/api/store';
import {
  SubscribedProductService,
  SubscriptionService as LicenseSubscriptionService
} from '@b3networks/api/subscription';
import { PurchaseStep, StepName, Topup2Component, Topup2Input } from '@b3networks/portal/shared';
import { DestroySubscriberComponent, X } from '@b3networks/shared/common';
import { ToastService } from '@b3networks/shared/ui/toast';
import { HashMap } from '@datorama/akita';
import { cloneDeep } from 'lodash';
import { firstValueFrom, forkJoin } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { NumberVendorModel } from '../select-number-product/select-number-product.component';
import { SkuInfo } from '../select-number/select-number.component';

const NUMBER_SKU_CODE = '78f90911-e6ef-4c79-afaa-f554fd20f9c5';

@Component({
  selector: 'psh-purchase-product',
  templateUrl: './purchase-product.component.html',
  styleUrls: ['./purchase-product.component.scss']
})
export class PurchaseProductComponent extends DestroySubscriberComponent implements OnInit {
  sellerOrgUUid: string;
  wallet: SellerWallet;
  steps: PurchaseStep[] = [];
  activeStep: StepName;
  stepName = StepName;
  numberSkuMapping: HashMap<SkuInfo> = {};
  type: string;
  organization: Organization;
  loadingPM = true;
  autoRenew = true;
  numberVendor = new NumberVendorModel();
  trial = false;
  hasTrial = false;
  totalActivationFee = 0;
  hasNumber = false;
  numberPrice = 0;
  product: Product;
  skuCode: string;
  saleModelCode: string;
  sku: Sku;
  saleModel: SalesModel;
  quantity: number;
  isProductPaymentDisabled: boolean;
  purchaseAddonModel: PurchaseAddonModel;
  loadingNumber: boolean = true;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  readonly KEYS = Object.keys;
  readonly NUMBER_SKU_CODE = NUMBER_SKU_CODE;
  readonly CPAAS_PRODUCT_ID = CPAAS_PRODUCT_ID;
  readonly APP_TYPE = 'APP';

  constructor(
    private routes: ActivatedRoute,
    private sellerWalletService: SellerWalletService,
    private linkedSellerService: LinkedSellerService,
    private dialog: MatDialog,
    private toastService: ToastService,
    private organizationQuery: OrganizationQuery,
    private orderService: OrderService,
    private router: Router,
    private numberService: NumberService,
    private licenseSubscriptionService: LicenseSubscriptionService,
    private productService: ProductService,
    private customerService: CustomerService,
    private subscriptionService: SubscribedProductService
  ) {
    super();
  }

  ngOnInit() {
    this.organizationQuery
      .selectOrganization(X.orgUuid)
      .pipe(
        takeUntil(this.destroySubscriber$),
        tap(org => {
          this.organization = org;
        })
      )
      .subscribe();

    this.routes.params.subscribe(params => {
      this.type = params['type'];
      this.skuCode = params['skuCode'];
      this.saleModelCode = params['saleModelCode'];

      const productId = params['id'];

      if (productId && this.skuCode) {
        this._initProductData(productId, this.skuCode);
      }
    });

    forkJoin([this.linkedSellerService.get(), this.sellerWalletService.getSellers()]).subscribe(
      ([sellerInfos, sellers]) => {
        const sellerOrgs = sellers.map(uuid => sellerInfos.find(i => i.uuid === uuid)).filter(s => s != null);
        if (sellerOrgs.length) {
          this.sellerOrgUUid = sellerOrgs[0].uuid;
          this._getWallet();
        }
      }
    );
  }

  private _getWallet() {
    this.sellerWalletService
      .getWallets(this.sellerOrgUUid)
      .pipe(map(res => res[0]))
      .pipe(takeUntil(this.destroySubscriber$))
      .subscribe(wallet => {
        this.wallet = wallet;
      });
  }

  private _initStep() {
    this.steps = [{ id: StepName.checkout, active: false, completed: false }];

    if (this.type === this.APP_TYPE && this.skuCode === NUMBER_SKU_CODE) {
      this.hasNumber = true;
      this.steps.unshift({ id: StepName.number, active: false, completed: false });
    }

    this.steps[0].active = true;
    this.activeStep = this.steps[0].id;
  }

  private async _initProductData(productId: string, skuCode: string) {
    this.hasTrial =
      (await firstValueFrom(this.subscriptionService.canSubscribeTrial(productId, skuCode)))?.trial ?? false;
    this.trial = this.hasTrial;
    this.product = await firstValueFrom(this.productService.getProduct(productId));
    this.sku = this.product.skus.find(sku => {
      return sku.code === this.skuCode && sku.salesModels.find(s => s.code === this.saleModelCode);
    });

    if (this.sku.code === NUMBER_SKU_CODE) {
      this.purchaseAddonModel = await firstValueFrom(
        this.customerService.getAddons(this.product.productId, this.sku.code, this.saleModelCode, X.orgUuid, '')
      );
    }

    this.quantity = 1;
    this.saleModel = this.sku.salesModels.find(s => s.code === this.saleModelCode);
    this._initStep();
    this.loadingPM = false;
  }

  changeProductQuantity(quantity: number) {
    this.isProductPaymentDisabled = quantity <= 0;

    if (quantity < 0) {
      return;
    }

    this.quantity = quantity;
  }

  openTopup() {
    const config: Topup2Input = {
      showAutoTopup: true,
      wallet: this.wallet,
      onlineOnly: true
    };

    this.dialog
      .open(Topup2Component, {
        width: '100%',
        height: '100%',
        maxWidth: '100%',
        maxHeight: '100%',
        data: config
      })
      .afterClosed()
      .subscribe(result => {
        if (result?.cancel) {
          return;
        }

        if (result?.error) {
          this.toastService.error(result?.error?.message);
        } else {
          this._getWallet();
        }
      });
  }

  back() {
    const currentActiveStep = this.steps.findIndex(s => s.active);
    if (currentActiveStep === 0) {
      this.cancel();
    } else {
      this.steps[currentActiveStep].active = false;
      this.steps[currentActiveStep].completed = true;
      this.steps[currentActiveStep - 1].active = true;
      this.activeStep = this.steps[currentActiveStep - 1].id;
    }
  }

  get totalPrice() {
    let total = 0;

    if (this.skuCode === NUMBER_SKU_CODE) {
      if (Object.keys(this.numberSkuMapping).length) {
        const numberSku = this.numberSkuMapping[Object.keys(this.numberSkuMapping)[0]];
        total += numberSku.price * numberSku.selectedNumbers.length + this.totalActivationFee;
      }
    } else {
      total = this.saleModel.amount * this.quantity;
    }

    return total;
  }

  private _getAddress() {
    if (!this.organization.billingInfo) {
      return '';
    }

    const arr = [];
    const temp = [];

    arr.push(this.organization.name);
    arr.push(this.organization.billingInfo.addressLineOne);
    arr.push(this.organization.billingInfo.addressLineTwo);
    temp.push(this.organization.billingInfo.city);
    temp.push(this.organization.billingInfo.state);
    temp.push(this.organization.billingInfo.zip);
    arr.push(temp.join(', '));

    return arr.join('\n');
  }

  payment() {
    if (this.type !== this.APP_TYPE) {
      this._productPayment();
    }

    if (this.type === this.APP_TYPE && this.product.productId === CPAAS_PRODUCT_ID) {
      this._cpaasPayment();
    }
  }

  _productPayment() {
    if (!['one_off', 'one_time_charge'].includes(this.saleModelCode)) {
      const items = [
        {
          autoRenew: this.autoRenew,
          multiplier: 1,
          productId: this.product.productId,
          quantity: this.quantity,
          saleModelCode: this.saleModelCode,
          sku: this.skuCode,
          trial: this.trial
        }
      ];

      const body: SubscriptionLicense = {
        items: items as BundleItem[]
      };

      this.licenseSubscriptionService.buySubscribe(body).subscribe(
        _ => this.router.navigate(['purchase', 'success']),
        _ => this.router.navigate(['purchase', 'fail'])
      );
    } else {
      const orderRequest = new OrderRequest();

      orderRequest.shippingAddress = this._getAddress();
      orderRequest.orderItems = [];
      orderRequest.orderItems.push(new OrderItem(this.product.productId, this.skuCode, 'one_off', this.quantity));

      this.orderService.purchaseOrder(orderRequest).subscribe(
        () => {
          this.router.navigate(['purchase', 'success']);
        },
        error => {
          this.router.navigate(['purchase', 'fail']);

          if ('billing.insufficientBalance' == error.code) {
            X.showWarn(error.message);
          } else {
            X.showWarn(
              'Sorry, an error has occurred when we try to fulfill your request. Please try again in a few minutes.'
            );
          }
        }
      );
    }
  }

  _cpaasPayment() {
    const items: any = [
      {
        autoRenew: this.autoRenew,
        multiplier: this.quantity,
        productId: this.product.productId,
        quantity: 1,
        saleModelCode: this.saleModelCode,
        sku: this.skuCode,
        trial: this.trial
      }
    ];

    //* NUMBER
    if (this.skuCode === NUMBER_SKU_CODE) {
      const numbers = this.numberSkuMapping[Object.keys(this.numberSkuMapping)[0]]?.selectedNumbers.map(s => s.number);

      items[0].numberProduct = this.numberVendor.selected.productId;
      items[0].numberSku = this.numberVendor.selected.selectedSku.sku;
      items[0].numbers = numbers;
      items[0].quantity = numbers.length;
    }

    const body: SubscriptionLicense = {
      items: items as BundleItem[]
    };

    this.licenseSubscriptionService.buySubscribe(body).subscribe(
      _ => this.router.navigate(['purchase', 'success']),
      _ => this.router.navigate(['purchase', 'fail'])
    );
  }

  cancel() {
    this.router.navigate([this.product.getNameInUrlPath(), 'variant'], {
      queryParams: { productId: this.product.productId }
    });
  }

  next() {
    if (this.activeStep === StepName.number) {
      this.numberService
        .checkMultiDocument(Object.keys(this.numberSkuMapping), X.orgUuid)
        .pipe(
          tap(check => {
            if (check[0].valid) {
              this._finishSelectNumber();
              this._nextStep();
              return;
            }

            const msg = `Document of ${check[0].sku} numbers is invalid`;
            this.toastService.warning(msg);
            throw new Error(msg);
          })
        )
        .subscribe();
    } else {
      this._nextStep();
    }
  }

  private _nextStep() {
    const currentActiveStep = this.steps.findIndex(s => s.active);
    if (currentActiveStep === this.steps.length - 1) {
      // last step
    } else {
      this.steps[currentActiveStep].active = false;
      this.steps[currentActiveStep].completed = true;
      this.steps[currentActiveStep + 1].active = true;
      this.activeStep = this.steps[currentActiveStep + 1].id;
    }
  }

  private _finishSelectNumber() {
    const selectedNumbers = this.numberSkuMapping[Object.keys(this.numberSkuMapping)[0]].selectedNumbers;
    const numbers = selectedNumbers.map(s => s.number);

    if (selectedNumbers?.length >= 1) {
      this.numberService.calcActivationFee(numbers).subscribe(fee => (this.totalActivationFee = fee));

      const seletectAvailableNumber = selectedNumbers.filter(item => {
        return item.state == 'Available';
      });

      if (seletectAvailableNumber && seletectAvailableNumber.length > 0) {
        const reserveNumberObservable = [];

        seletectAvailableNumber.forEach(item => {
          reserveNumberObservable.push(this.numberService.reserveNumberV3(item.number, X.orgUuid));
        });

        forkJoin(reserveNumberObservable).subscribe(
          _ => {
            const cloneSelectedNumbers = cloneDeep(selectedNumbers);
            cloneSelectedNumbers.forEach(item => {
              item.state = 'Reserved';
            });
            this.numberSkuMapping[Object.keys(this.numberSkuMapping)[0]].selectedNumbers = cloneSelectedNumbers;
          },
          error => {
            console.log(error);
            this.router.navigate(['purchase', 'step-fail'], {
              state: { msg: 'Sorry, an error has occurred when we try to fulfill your request. Please try again.' }
            });
          }
        );
      }
    } else {
      const msg = `You must select at least 1 number`;
      this.toastService.warning(msg);
      throw new Error(msg);
    }
  }

  changeNumberSkuMapping(numberSkuMapping: HashMap<SkuInfo>) {
    this.numberSkuMapping = numberSkuMapping;
    const numberSkus = Object.keys(this.numberSkuMapping);
    this.numberPrice = numberSkus.length ? this.numberSkuMapping[numberSkus[0]].price : 0;
  }

  changeNumberVendor(numberVendor: NumberVendorModel) {
    this.numberVendor = numberVendor;
  }

  changeLoadingNumber(loadingNumber: boolean) {
    this.loadingNumber = loadingNumber;
  }

  changeTrial() {
    this.trial = !this.trial;
  }
}
